// Bloco reutilizável de gestão de pacotes (templates "X aulas por R$ Y")
// dentro de uma disciplina. Renderizado no detalhe da disciplina.
// CRUD inline simples: listar, criar, editar, arquivar.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Archive,
  Check,
  Edit2,
  Loader2,
  Package,
  Plus,
  X
} from "lucide-react";
import { Switch } from "@app/ui/components/switch";
import { api, ApiError, type PacoteRow } from "../api";
import { openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const phase = signal<"idle" | "loading" | "ready" | "error">("idle");
const items = signal<PacoteRow[]>([]);
const errMsg = signal<string | null>(null);
const loadedFor = signal<string | null>(null);

async function load(disciplinaId: string) {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listPacotes(disciplinaId);
    items.value = r.pacotes;
    loadedFor.value = disciplinaId;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

export function PacotesSection({ disciplinaId }: { disciplinaId: string }) {
  useEffect(() => {
    if (loadedFor.value !== disciplinaId) {
      load(disciplinaId);
    }
  }, [disciplinaId]);

  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  if (phase.value === "loading" && loadedFor.value !== disciplinaId) {
    return (
      <div class="rounded-md border border-border bg-card/40 p-4">
        <p class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando pacotes…
        </p>
      </div>
    );
  }

  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 p-4">
        <p class="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="size-4" />
          {errMsg.value}
        </p>
      </div>
    );
  }

  const list = items.value.filter((p) => !p.archivedAt);
  const archived = items.value.filter((p) => !!p.archivedAt);

  return (
    <section class="rounded-md border border-border bg-card/40 p-4">
      <div class="mb-3 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <Package className="size-4 text-primary" />
          <h3 class="text-sm font-bold">Pacotes</h3>
          <span class="text-xs text-muted-foreground">
            ({list.length} ativo{list.length !== 1 ? "s" : ""})
          </span>
        </div>
        {!creating && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            class="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20"
          >
            <Plus className="size-3.5" />
            Novo pacote
          </button>
        )}
      </div>

      {creating && (
        <PacoteForm
          disciplinaId={disciplinaId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load(disciplinaId);
          }}
        />
      )}

      {list.length === 0 && !creating && (
        <p class="rounded-md border border-dashed border-border bg-background/40 px-4 py-6 text-center text-xs text-muted-foreground">
          Nenhum pacote ainda. Crie o primeiro pra liberar compras pelos alunos.
        </p>
      )}

      <ul class="mt-2 space-y-2">
        {list.map((p) =>
          editingId === p.id ? (
            <li key={p.id}>
              <PacoteForm
                disciplinaId={disciplinaId}
                editing={p}
                onClose={() => setEditingId(null)}
                onSaved={() => {
                  setEditingId(null);
                  load(disciplinaId);
                }}
              />
            </li>
          ) : (
            <PacoteRowItem
              key={p.id}
              p={p}
              onEdit={() => setEditingId(p.id)}
              onArchived={() => load(disciplinaId)}
            />
          )
        )}
      </ul>

      {archived.length > 0 && (
        <details class="mt-3">
          <summary class="cursor-pointer text-[11px] font-medium text-muted-foreground hover:text-foreground">
            Arquivados ({archived.length})
          </summary>
          <ul class="mt-2 space-y-1.5">
            {archived.map((p) => (
              <li
                key={p.id}
                class="flex items-center gap-2 rounded-md border border-border bg-background/40 px-3 py-1.5 text-[11px] text-muted-foreground"
              >
                <Archive className="size-3.5" />
                <span class="flex-1 truncate">{p.nome}</span>
                <span>{p.numAulas} aulas · {formatBRL(p.precoCents)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function PacoteRowItem({
  p,
  onEdit,
  onArchived
}: {
  p: PacoteRow;
  onEdit: () => void;
  onArchived: () => void;
}) {
  const [busy, setBusy] = useState(false);
  async function archive() {
    if (!confirm(`Arquivar pacote "${p.nome}"? Compras existentes não são afetadas.`)) return;
    setBusy(true);
    try {
      await api.archivePacote(p.id);
      onArchived();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }
  const valorUnit = p.numAulas > 0 ? p.precoCents / p.numAulas : 0;
  return (
    <li class="flex flex-wrap items-center gap-3 rounded-md border border-border bg-background/60 px-4 py-3">
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <span class="text-sm font-bold">{p.nome}</span>
          {!p.ativo && (
            <span class="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              Inativo
            </span>
          )}
        </div>
        <div class="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
          <span class="font-semibold text-foreground">
            {p.numAulas} aulas
          </span>
          <span>·</span>
          <span class="font-semibold text-foreground">
            {formatBRL(p.precoCents)}
          </span>
          <span class="opacity-50">
            ({formatBRL(valorUnit)} / aula)
          </span>
          {p.descricao && (
            <>
              <span>·</span>
              <span class="truncate">{p.descricao}</span>
            </>
          )}
        </div>
      </div>
      <div class="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          onClick={onEdit}
          title="Editar"
          aria-label="Editar pacote"
          class="grid size-8 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:border-primary/40 hover:bg-accent hover:text-foreground"
        >
          <Edit2 className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={archive}
          disabled={busy}
          title="Arquivar"
          aria-label="Arquivar pacote"
          class="grid size-8 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-600 disabled:opacity-50"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Archive className="size-3.5" />}
        </button>
      </div>
    </li>
  );
}

function PacoteForm({
  disciplinaId,
  editing,
  onClose,
  onSaved
}: {
  disciplinaId: string;
  editing?: PacoteRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [nome, setNome] = useState(editing?.nome ?? "");
  const [descricao, setDescricao] = useState(editing?.descricao ?? "");
  const [numAulas, setNumAulas] = useState(editing?.numAulas ?? 5);
  // Mostra em reais no input, converte pra cents no submit
  const [precoReais, setPrecoReais] = useState(
    editing ? (editing.precoCents / 100).toFixed(2) : ""
  );
  const [ativo, setAtivo] = useState(editing?.ativo ?? true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setErr(null);
    if (!nome.trim()) {
      setErr("Nome obrigatório.");
      return;
    }
    const precoCents = Math.round(parseFloat(precoReais.replace(",", ".")) * 100);
    if (!Number.isFinite(precoCents) || precoCents < 0) {
      setErr("Preço inválido.");
      return;
    }
    if (!Number.isFinite(numAulas) || numAulas < 1) {
      setErr("Quantidade de aulas inválida.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patchPacote(editing.id, {
          nome: nome.trim(),
          descricao: descricao.trim() || null,
          numAulas: Math.floor(numAulas),
          precoCents,
          ativo
        });
      } else {
        await api.createPacote({
          disciplinaId,
          nome: nome.trim(),
          descricao: descricao.trim() || null,
          numAulas: Math.floor(numAulas),
          precoCents,
          ativo
        });
      }
      onSaved();
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="rounded-md border border-primary/40 bg-primary/5 p-4">
      <div class="mb-2 flex items-center justify-between">
        <span class="text-xs font-bold uppercase tracking-wider text-primary">
          {editing ? "Editar pacote" : "Novo pacote"}
        </span>
        <button
          type="button"
          onClick={onClose}
          title="Cancelar"
          aria-label="Cancelar"
          class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </div>
      <div class="grid gap-2 md:grid-cols-2">
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Nome
          </span>
          <input
            type="text"
            value={nome}
            onInput={(e) => setNome((e.target as HTMLInputElement).value)}
            placeholder="ex: Pacote 5 aulas"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </label>
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Descrição (opcional)
          </span>
          <input
            type="text"
            value={descricao}
            onInput={(e) => setDescricao((e.target as HTMLInputElement).value)}
            placeholder="detalhes"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </label>
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Nº de aulas
          </span>
          <input
            type="number"
            min={1}
            max={999}
            value={numAulas}
            onInput={(e) => setNumAulas(Number((e.target as HTMLInputElement).value) || 0)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </label>
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Preço total (R$)
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={precoReais}
            onInput={(e) => setPrecoReais((e.target as HTMLInputElement).value)}
            placeholder="250.00"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono outline-none focus:border-primary"
          />
        </label>
      </div>
      <label class="mt-3 inline-flex items-center gap-2 text-xs">
        <Switch checked={ativo} onCheckedChange={(v) => setAtivo(v)} />
        Ativo (alunos podem comprar)
      </label>
      {err && (
        <div class="mt-2 flex items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-[11px] text-destructive">
          <AlertCircle className="size-3.5" />
          {err}
        </div>
      )}
      <div class="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Check className="size-3.5" />}
          {editing ? "Salvar" : "Criar"}
        </button>
      </div>
    </div>
  );
}
